/**
 * Admin Hub Sidebar Component
 * Collapsible sidebar containing tree navigation
 */
import React, { useRef, useEffect } from 'react';
import { Box, IconButton, Tooltip, Divider, Typography, TextField, InputAdornment } from '@mui/material';
import {
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Dashboard as DashboardIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAdminHub } from './context/AdminHubContext';
import { AdminHubTreeNav } from './AdminHubTreeNav';
import { AdminHubQuickAccess } from './AdminHubQuickAccess';
import { ADMIN_HUB_DESIGN } from '../../config/adminHubConfig';

interface AdminHubSidebarProps {
  isMobile?: boolean;
}

export const AdminHubSidebar: React.FC<AdminHubSidebarProps> = ({ isMobile = false }) => {
  const {
    sidebarCollapsed,
    toggleSidebarCollapse,
    navigateTo,
    activePath,
    expandAllNodes,
    collapseAllNodes,
    filterText,
    setFilterText,
    clearFilter,
    setSidebarCollapsed,
  } = useAdminHub();

  const filterInputRef = useRef<HTMLInputElement>(null);

  // Handle "/" keyboard shortcut to focus filter
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // "/" to focus filter (when not in an input)
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        if (sidebarCollapsed) {
          setSidebarCollapsed(false);
        }
        // Small delay to allow sidebar to expand
        setTimeout(() => {
          filterInputRef.current?.focus();
        }, 50);
      }
      // Escape to clear filter
      if (e.key === 'Escape' && filterText) {
        clearFilter();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarCollapsed, setSidebarCollapsed, filterText, clearFilter]);

  const handleSearchIconClick = () => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      setTimeout(() => {
        filterInputRef.current?.focus();
      }, 200);
    } else {
      filterInputRef.current?.focus();
    }
  };

  const sidebarWidth = sidebarCollapsed
    ? ADMIN_HUB_DESIGN.SIDEBAR_WIDTH_COLLAPSED
    : ADMIN_HUB_DESIGN.SIDEBAR_WIDTH_EXPANDED;

  const isDashboardActive = activePath === '/admin/hub';

  return (
    <Box
      component="nav"
      sx={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        height: '100%',
        backgroundColor: ADMIN_HUB_DESIGN.COLORS.SIDEBAR_BG,
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: `width ${ADMIN_HUB_DESIGN.ANIMATION_DURATION}ms ease`,
        overflow: 'hidden',
      }}
    >
      {/* Header with collapse toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          px: sidebarCollapsed ? 0 : 2,
          py: 1.5,
          minHeight: 48,
        }}
      >
        {!sidebarCollapsed && (
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: '0.75rem',
            }}
          >
            Admin Hub
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!sidebarCollapsed && (
            <>
              <Tooltip title="Expand all">
                <IconButton
                  size="small"
                  onClick={expandAllNodes}
                  sx={{
                    color: 'text.secondary',
                    padding: 0.5,
                    '&:hover': {
                      color: 'text.primary',
                    },
                  }}
                >
                  <ExpandAllIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse all">
                <IconButton
                  size="small"
                  onClick={collapseAllNodes}
                  sx={{
                    color: 'text.secondary',
                    padding: 0.5,
                    '&:hover': {
                      color: 'text.primary',
                    },
                  }}
                >
                  <CollapseAllIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {!isMobile && (
            <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <IconButton
                size="small"
                onClick={toggleSidebarCollapse}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
              >
                {sidebarCollapsed ? <ExpandIcon /> : <CollapseIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Filter input */}
      {!sidebarCollapsed ? (
        <Box sx={{ px: 1.5, py: 1 }}>
          <TextField
            inputRef={filterInputRef}
            size="small"
            placeholder="Filter... (press /)"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: filterText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearFilter}
                    sx={{ padding: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                fontSize: '0.875rem',
                backgroundColor: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'text.secondary',
                },
              },
            }}
            sx={{
              '& .MuiInputBase-input': {
                py: 0.75,
              },
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1,
          }}
        >
          <Tooltip title="Search navigation (press /)">
            <IconButton
              size="small"
              onClick={handleSearchIconClick}
              sx={{ color: 'text.secondary' }}
            >
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Divider sx={{ mb: 0.5 }} />

      {/* Dashboard link */}
      <Box
        onClick={() => navigateTo('/admin/hub')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: sidebarCollapsed ? 0 : 2,
          py: 1,
          cursor: 'pointer',
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          backgroundColor: isDashboardActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG : 'transparent',
          borderLeft: isDashboardActive ? `3px solid ${ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM}` : '3px solid transparent',
          '&:hover': {
            backgroundColor: isDashboardActive
              ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM_BG
              : ADMIN_HUB_DESIGN.COLORS.HOVER_BG,
          },
          transition: 'background-color 150ms ease',
        }}
      >
        <DashboardIcon
          sx={{
            fontSize: 20,
            color: isDashboardActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM : 'text.secondary',
          }}
        />
        {!sidebarCollapsed && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: isDashboardActive ? 500 : 400,
              color: isDashboardActive ? ADMIN_HUB_DESIGN.COLORS.ACTIVE_ITEM : 'text.primary',
            }}
          >
            Dashboard
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 0.5 }} />

      {/* Quick Access - only show when sidebar is expanded and not filtering */}
      {!sidebarCollapsed && !filterText && (
        <AdminHubQuickAccess />
      )}

      {/* Tree Navigation */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          py: 1,
        }}
      >
        <AdminHubTreeNav collapsed={sidebarCollapsed} />
      </Box>
    </Box>
  );
};

export default AdminHubSidebar;
